"use client";

import type { ExploreBriefCard } from "../types";
import { HighlightedText } from "./highlighted-text";
import { type BriefSurface, useBriefRowTracking } from "./use-brief-analytics";

/**
 * Compact single-line brief row used in both the default feed timeline
 * (grouped by category) and the search results list (flat by relevance).
 * Title on the left; tag pills on the right; click opens the brief detail
 * dialog at the parent level.
 *
 * Wires up `brief_card_viewed` / `brief_card_clicked` analytics via
 * `useBriefRowTracking` — caller passes the surface label and the row's
 * 0-based position so events carry consistent context across surfaces.
 */
export function CompactBriefRow({
  brief,
  positionInList,
  surface,
  onClick,
  highlightQuery,
}: {
  brief: ExploreBriefCard;
  positionInList: number;
  surface: BriefSurface;
  onClick: () => void;
  highlightQuery?: string;
}) {
  const { ref, handleClick } = useBriefRowTracking({
    brief,
    surface,
    positionInList,
    onClick,
  });
  return (
    <button
      className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-[var(--explore-border-light)]"
      onClick={handleClick}
      ref={ref}
      style={{ fontFamily: "var(--homepage-sans-stack)" }}
      type="button"
    >
      <p className="min-w-0 flex-1 font-medium text-[0.82rem] text-[var(--explore-text-primary)] leading-[1.4] max-sm:line-clamp-2 sm:truncate">
        <HighlightedText query={highlightQuery} text={brief.title} />
      </p>
      {brief.tags.length > 0 && (
        <div className="hidden shrink-0 items-center gap-1 sm:flex">
          {brief.tags.map((tag) => (
            <span
              className="rounded-full bg-[var(--explore-tag-bg)] px-2 py-0.5 text-[0.65rem] text-[var(--explore-tag-text)]"
              key={tag.slug}
            >
              {tag.displayName}
            </span>
          ))}
        </div>
      )}
    </button>
  );
}
